import { EventInput } from '@fullcalendar/common';
import { Button, Stack } from '@mui/material';
import { addMonths, endOfMonth, parse, startOfMonth, sub, subMonths } from 'date-fns';
import { get, map } from 'lodash';
import { ComponentType, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hslById } from '../../../common/utils/colors-utils';
import { dateFormat, useAuth, useDialog, useResources } from '../../../hooks';
import { getSchedules } from '../../../thunks/schedule-thunks';
import { AttestationsEditDialog } from '../../attestations';
import { PlusIcon } from '../../icons';
import { Page } from '../../page';
import CalendarCells from '../CalendarCells';
import CalendarHeader from '../CalendarHeader';
import { SchedulesPanel } from '../schedules-panel';

export const SchedulesAdmin: ComponentType = () => {
  const { t } = useTranslation();
  const { isSuperAdmin } = useAuth();
  const [openAttestationsEdit, closeAttestationsEdit] = useDialog(AttestationsEditDialog);

  const currentDate = new Date();
  const [currentMonth, setCurrentMonth] = useState<Date>(currentDate);

  const nextMonth = () => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  };

  const prevMonth = () => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  };

  const {
    list: { hits },
    query,
    handleQueryUpdate,
    handleFetchCurrentPage,
  } = useResources({
    entity: 'schedule',
    onGetAll: getSchedules,
    defaultParams: {
      take: 99999,
      embed: 'organization',
      dateFrom: dateFormat(startOfMonth(new Date()), 'Y-M-D'),
      dateTo: dateFormat(endOfMonth(new Date()), 'Y-M-D'),
    },
    paging: false,
  });

  const handleCreate = (selectedDate: string = '') => {
    const now = new Date();
    const currentSelectedDate = parse(selectedDate, 'yyyy-MM-dd', new Date());
    if (currentSelectedDate < sub(now, { days: 1 })) {
      return;
    }

    openAttestationsEdit({
      title: t('attestations.add', 'Добавить аттестацию'),
      date: selectedDate,
      onSubmit: () => {
        closeAttestationsEdit();
        handleFetchCurrentPage();
      },
    });
  };

  const events: EventInput[] = map(hits, (item) => {
    const { id } = item;
    const startDate = new Date(item.date);
    return {
      id,
      title: get(item, ['organization', 'title'], ''),
      start: item.date,
      end: new Date(startDate.setHours(startDate.getHours() + 1)).toISOString(),
      allDay: false,
      backgroundColor: hslById(get(item, ['organization', 'id'], 1)),
    };
  });

  return (
    <Page
      title={t('schedules', 'Графики')}
      topButton={
        <Button
          onClick={() => handleCreate()}
          variant="contained"
          color="secondary"
          startIcon={<PlusIcon />}
          size="small"
        >
          {t('schedules.add', 'Добавить графики')}
        </Button>
      }
    >
      {isSuperAdmin && (
        <SchedulesPanel {...{ organization: query.organization, onUpdate: handleQueryUpdate }} />
      )}

      <Stack gap={0.25}>
        <CalendarHeader prevMonth={prevMonth} nextMonth={nextMonth} currentMonth={currentMonth} />
        <CalendarCells
          month={currentMonth}
          onDayClick={handleCreate}
          data={events}
          refetch={handleFetchCurrentPage}
        />
      </Stack>
      {/* <Calendar events={events} /> */}
      {/* {false && <SchedulesCalendar {...{ config, events }} addable />} */}
    </Page>
  );
};
